import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import 'react-international-phone/style.css'
import './InternationalPhone.scss'
import PhoneInput from 'react-phone-number-input/input'
import { parsePhoneNumber, isSupportedCountry, getCountries } from 'react-phone-number-input'
import { getCountryCallingCode } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'
import * as flagSvgs from 'country-flag-icons/string/3x2'
import { convert3DigitCountryCodeTo2Digit } from '@utils/convert3DigitCountryCodeTo2Digit'
import { getCountryName } from '@utils/getCountryName'

const FLAG_SVGS = flagSvgs as Record<string, string>

function getFlagUrl(iso2: string): string {
  const upper = iso2.toUpperCase()
  if (upper.length !== 2) return ''
  const svg = FLAG_SVGS[upper]
  if (!svg) return ''
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

export interface ParsedCountry {
  name?: string
  iso2: string
  dialCode: string
  format?: string
  priority?: number
  areaCodes?: string[]
}

export interface InternationalPhoneProps {
  className?: string
  defaultCountry?: string
  error?: boolean | string
  id?: string
  label: string
  name?: string
  onBlur: () => void
  onFocus?: () => void
  onChange: (phone: string, meta?: { country: ParsedCountry; inputValue: string }) => void
  required?: boolean
  value?: string
  /** When true, do not include dial code or prefix inside the input field (display-only outside). Default: false */
  disableDialCodeAndPrefix?: boolean
  /** When true, show the disabled dial code/prefix outside the input. Works only if disableDialCodeAndPrefix is true. Default: true */
  showDisabledDialCodeAndPrefix?: boolean
  limitToUsaNumbers?: boolean
}

export const InternationalPhone: React.FC<InternationalPhoneProps> = ({
  className,
  defaultCountry = 'us',
  error = false,
  id,
  label,
  name,
  onBlur,
  onFocus,
  onChange,
  required,
  value,
  disableDialCodeAndPrefix = false,
  showDisabledDialCodeAndPrefix = true,
  limitToUsaNumbers = false,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const resolvedDefaultCountry = useMemo(() => {
    if (!defaultCountry) return undefined
    const normalized = defaultCountry.trim()
    if (!normalized) return undefined
    const iso2 = convert3DigitCountryCodeTo2Digit(normalized) || normalized
    const normalizedIso2 = iso2.toUpperCase()
    return isSupportedCountry(normalizedIso2) ? (normalizedIso2 as CountryCode) : undefined
  }, [defaultCountry])

  const resolvedCountry = useMemo(() => {
    if (limitToUsaNumbers) {
      return 'US' as CountryCode
    }
    return undefined
  }, [limitToUsaNumbers])

  const countriesList = useMemo(() => {
    const base = limitToUsaNumbers ? (['US'] as CountryCode[]) : (getCountries() as CountryCode[])
    return base.map((iso2) => ({
      iso2,
      name: getCountryName(iso2) || iso2,
      dialCode: getCountryCallingCode(iso2),
    }))
  }, [limitToUsaNumbers])

  const inferredCountryFromValue = useMemo(() => {
    if (!value) return undefined
    try {
      // Try parsing the incoming value directly. If it is a national-format
      // value (no leading '+') also try parsing it by prefixing the
      // resolved default country calling code so we can infer the country.
      const direct = parsePhoneNumber(value)
      if (direct && direct.country) return direct.country as CountryCode
      if (resolvedDefaultCountry) {
        try {
          const digits = value.replace(/\D/g, '')
          const prefixed = `+${getCountryCallingCode(resolvedDefaultCountry)}${digits}`
          return parsePhoneNumber(prefixed)?.country as CountryCode | undefined
        } catch (e) {
          return undefined
        }
      }
      return undefined
    } catch (e) {
      return undefined
    }
  }, [value, resolvedDefaultCountry])

  const [selectedCountry, setSelectedCountry] = useState<CountryCode | undefined>(resolvedCountry || inferredCountryFromValue || resolvedDefaultCountry)

  useEffect(() => {
    setSelectedCountry(resolvedCountry || inferredCountryFromValue || resolvedDefaultCountry)
  }, [resolvedCountry, inferredCountryFromValue, resolvedDefaultCountry])

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!dropdownRef.current) return
      if (!dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  const selectedDialCode = useMemo(() => {
    if (!selectedCountry) return ''
    try {
      return getCountryCallingCode(selectedCountry)
    } catch (e) {
      return ''
    }
  }, [selectedCountry])

  const handlePhoneChange = useCallback(
    (nextValue?: string) => {
      let parsedCountry: ParsedCountry | undefined
      if (nextValue) {
        try {
          const parsed = parsePhoneNumber(nextValue)
          const iso2 = parsed?.country
          if (iso2) {
            parsedCountry = {
              iso2: iso2.toLowerCase(),
              dialCode: getCountryCallingCode(iso2),
            }
          }
        } catch (e) {
          parsedCountry = undefined
        }
      }

      const fallbackCountry = selectedCountry
        ? {
            iso2: selectedCountry.toLowerCase(),
            dialCode: selectedDialCode,
          }
        : undefined

      onChange(nextValue || '', {
        country: (parsedCountry || fallbackCountry) as ParsedCountry,
        inputValue: nextValue || '',
      })
    },
    [onChange, selectedCountry, selectedDialCode],
  )

  const handleCountrySelect = useCallback((country: CountryCode) => {
    setSelectedCountry(country)
    setIsDropdownOpen(false)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false)
      e.preventDefault()
    }
  }, [])

  const effectiveCountry = selectedCountry || resolvedDefaultCountry
  const currentCountryLower = effectiveCountry ? effectiveCountry.toLowerCase() : 'us'
  const phoneInputCountry = selectedCountry
  const phoneInputDefaultCountry = phoneInputCountry ? undefined : resolvedDefaultCountry

  // Normalize incoming `value` so the underlying `PhoneInput` receives
  // either an E.164 string or `undefined`. Many forms in the app pass a
  // national-format number (no leading '+'); convert those to E.164 using
  // the effective country so the input shows the saved value in edit mode.
  const phoneInputValue = useMemo(() => {
    if (!value) return undefined
    const trimmed = String(value).trim()
    if (!trimmed) return undefined
    if (trimmed.startsWith('+')) return trimmed
    const country = selectedCountry || resolvedDefaultCountry
    if (!country) return trimmed
    const digits = trimmed.replace(/\D/g, '')
    if (!digits) return undefined
    try {
      return `+${getCountryCallingCode(country)}${digits}`
    } catch (e) {
      return trimmed
    }
  }, [value, selectedCountry, resolvedDefaultCountry])

  const showExternalDialCode = disableDialCodeAndPrefix && showDisabledDialCodeAndPrefix

  const hasPhoneValue = String(value ?? '').trim() !== ''

  return (
    <div
      className={clsx('international-phone', className, {
        'international-phone--error': error,
        'international-phone--dial-preview': showExternalDialCode,
      })}>
      <label
        className={clsx('international-phone__label', { 'international-phone__label--reduced': hasPhoneValue })}
        htmlFor={id}>
        {label}
        {required && <> *</>}
      </label>
      <div
        className={clsx('react-international-phone-input-container international-phone__input-wrapper', {
          'international-phone__input-wrapper--error': error,
        })}>
        <div
          className="react-international-phone-country-selector"
          ref={dropdownRef}>
          <button
            title={getCountryName(effectiveCountry || 'US') || ''}
            aria-label="Country selector"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            type="button"
            className="react-international-phone-country-selector-button"
            data-country={currentCountryLower}
            onClick={() => setIsDropdownOpen((v) => !v)}>
            <div className="react-international-phone-country-selector-button__button-content">
              <img
                className="react-international-phone-flag-emoji react-international-phone-country-selector-button__flag-emoji"
                src={getFlagUrl(effectiveCountry || 'US')}
                draggable={false}
                data-country={currentCountryLower}
                loading="lazy"
                alt=""
              />
              <div className="react-international-phone-country-selector-button__dropdown-arrow" />
            </div>
          </button>
          <ul
            role="listbox"
            className="react-international-phone-country-selector-dropdown"
            aria-label="Country list"
            tabIndex={0}
            style={{ display: isDropdownOpen ? 'block' : 'none' }}
            onKeyDown={handleKeyDown}>
            {countriesList.map((country) => {
              const isSelected = country.iso2 === effectiveCountry
              const iso2Lower = country.iso2.toLowerCase()
              return (
                <li
                  key={country.iso2}
                  data-country={iso2Lower}
                  role="option"
                  aria-selected={isSelected}
                  aria-label={`${country.name} +${country.dialCode}`}
                  id={`react-international-phone__${iso2Lower}-option`}
                  tabIndex={isSelected ? 0 : -1}
                  className={clsx('react-international-phone-country-selector-dropdown__list-item', {
                    'react-international-phone-country-selector-dropdown__list-item--selected': isSelected,
                  })}
                  title={country.name}
                  onClick={() => handleCountrySelect(country.iso2)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleCountrySelect(country.iso2)
                      e.preventDefault()
                    }
                  }}>
                  <img
                    className="react-international-phone-flag-emoji react-international-phone-country-selector-dropdown__list-item-flag-emoji"
                    src={getFlagUrl(country.iso2)}
                    draggable={false}
                    data-country={iso2Lower}
                    loading="lazy"
                    alt=""
                  />
                  <span className="react-international-phone-country-selector-dropdown__list-item-country-name">{country.name}</span>
                  <span className="react-international-phone-country-selector-dropdown__list-item-dial-code">+{country.dialCode}</span>
                </li>
              )
            })}
          </ul>
        </div>

        {disableDialCodeAndPrefix && showDisabledDialCodeAndPrefix && (
          <div className="react-international-phone-dial-code-preview">{selectedDialCode ? `+${selectedDialCode}` : ''}</div>
        )}

        <PhoneInput
          type="tel"
          className="react-international-phone-input"
          defaultCountry={phoneInputDefaultCountry}
          country={phoneInputCountry}
          international={disableDialCodeAndPrefix ? true : undefined}
          withCountryCallingCode={disableDialCodeAndPrefix ? false : undefined}
          required={required}
          name={name}
          id={id}
          data-defaultcountry={defaultCountry ? defaultCountry.toLowerCase() : undefined}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={handlePhoneChange}
          value={phoneInputValue}
        />
      </div>
      {error && <div className="international-phone__error-text">{error}</div>}
    </div>
  )
}
